import { useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { waitForImage } from 'lib/files';
import { LoadingIndicator } from 'components/common';
import { FileUploader } from 'components/common/uploader';
import {
  defaultBlockData,
  EditorBlockTranslationReplace
} from 'components/editor/blocks';
import { editorSocket } from 'components/editor/socket';
import {
  EditorBlockImageContent,
  EditorBlockImageControls
} from 'components/editor/blocks/image';
import { scrollToElement } from 'lib/common';
import { getBlockFileForLanguage } from 'lib/courses';

import type { BlockImage } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorChapter' | 'editorBlockIdForCropping' | 'editorLanguageActive'
>;

let Croppr;
if (typeof window !== 'undefined') {
  Croppr = require('croppr');
}

const EditorBlockImage = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockImage> & StateProps) => {
  const imageWrapper = useRef<HTMLDivElement>(undefined);
  const cropInstance = useRef<any>(undefined);

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const { editorChapter, editorLanguageActive, editorBlockIdForCropping } =
    stateProps;

  const altUpdateTimeout = useRef<NodeJS.Timeout>(undefined);
  const updateTimeout = useRef<NodeJS.Timeout>(undefined);

  const [altTextVisible, setAltTextVisible] = useState(
    Boolean(data.locales?.[languageVersion]?.translatable?.altText)
  );
  const [crop, setCrop] = useState(false);
  const [imageVisible, setImageVisible] = useState(false);
  const [imageWrapperHeight, setImageWrapperHeight] = useState(0);
  const [maxWidth, setMaxWidth] = useState(
    data.locales[languageVersion].maxWidth || 100
  );
  const [setSize, setSetSize] = useState(false);
  const [replace, setReplace] = useState(false);
  const [showLanguageSpecificUploader, setShowLanguageSpecificUploader] =
    useState(false);

  const { shouldShowDefaultFile: shouldShowDefaultImage, fileUrl: imageUrl } =
    getBlockFileForLanguage({
      data,
      courseLanguage: course.language,
      editorActiveLanguageVersion: editorLanguageActive,
      showLanguageSpecificUploader
    });

  useEffect(() => {
    if (editorBlockIdForCropping !== blockId) setCrop(false);
  }, [editorBlockIdForCropping]);

  useEffect(() => {
    if (data.locales[languageVersion].maxWidth !== maxWidth)
      setMaxWidth(data.locales[languageVersion].maxWidth || 100);
  }, [data.locales[languageVersion].maxWidth]);

  useEffect(() => {
    if (
      languageVersion &&
      shouldShowDefaultImage &&
      !altTextVisible &&
      data.locales?.[course.language]?.translatable?.altText
    ) {
      setAltTextVisible(true);
    }
  }, [shouldShowDefaultImage]);

  useEffect(() => {
    return () => {
      clearTimeout(altUpdateTimeout.current);
      clearTimeout(updateTimeout.current);
    };
  }, []);

  const handleAltTextChange = (newAltText: string) => {
    clearTimeout(altUpdateTimeout.current);
    altUpdateTimeout.current = setTimeout(() => {
      updateData({
        ...data,
        locales: {
          ...data.locales,
          [languageVersion]: {
            ...data.locales[languageVersion],
            translatable: {
              ...data.locales[languageVersion].translatable,
              altText: newAltText
            }
          }
        }
      });
    }, 500);
  };

  const handleAltTextToggle = () => {
    if (altTextVisible) {
      setAltTextVisible(false);
      if (data.locales[languageVersion].translatable.altText) {
        updateData({
          ...data,
          locales: {
            ...data.locales,
            [languageVersion]: {
              ...data.locales[languageVersion],
              translatable: {
                ...data.locales[languageVersion].translatable,
                altText: ''
              }
            }
          }
        });
      }
    } else {
      setAltTextVisible(true);
    }
  };

  const handleCaptionChange = (newCaption: string) => {
    clearTimeout(updateTimeout.current);
    updateTimeout.current = setTimeout(() => {
      updateData({
        ...data,
        locales: {
          ...data.locales,
          [languageVersion]: {
            ...data.locales[languageVersion],
            translatable: {
              ...data.locales[languageVersion].translatable,
              caption: newCaption
            }
          }
        }
      });
    }, 500);
  };

  const handleCropCancel = () => {
    cropInstance.current.destroy();
    setCrop(false);
  };

  const handleCropClick = async () => {
    const img = imageWrapper.current.querySelector('img');
    await waitForImage(img.src);
    const isSvg = /\.svg$/.test(img.src);
    cropInstance.current = new Croppr(img, {
      returnMode: isSvg ? 'raw' : 'real',
      startSize: [60, 60]
    });

    setCrop(true);
  };

  const handleCropSave = async () => {
    const { containerEl, imageEl } = cropInstance.current;
    const isSvg = /\.svg$/.test(imageEl.currentSrc);
    let cropData = cropInstance.current.getValue();

    if (isSvg) {
      // Svg image in safari do not crop correctly because safari returns
      // incorrect values for img.naturalHeight and img.naturalWidth. This is
      // a workaround to get correct values even in Safari.
      cropData = await new Promise((resolve) => {
        let img = new Image(1, 1);
        img.src = imageEl.currentSrc;
        img.onload = () => {
          const data = {
            x: (cropData.x * img.naturalWidth) / containerEl.offsetWidth,
            y: (cropData.y * img.naturalHeight) / containerEl.offsetHeight,
            width:
              (cropData.width * img.naturalWidth) / containerEl.offsetWidth,
            height:
              (cropData.height * img.naturalHeight) / containerEl.offsetHeight
          };
          img = null;
          resolve(data);
        };
        img.onerror = () => {
          img = null;
          resolve(cropData);
        };
      });
    }

    cropInstance.current.destroy();

    editorSocket.cropImage({
      blockId,
      chapterId: editorChapter.id,
      cropData,
      imageUrl: data.locales[languageVersion].file.url,
      languageVersion: languageVersion
    });
  };

  const handleImageLoad = () => {
    setImageVisible(true);
  };

  const handleLanguageVersionReplaceCancel = () => {
    setImageVisible(true);
    setShowLanguageSpecificUploader(false);
  };

  const handleLanguageVersionReplaceClick = () => {
    setImageVisible(false);
    setShowLanguageSpecificUploader(true);
  };

  const handleSetSizeCancel = () => {
    setMaxWidth(data.locales?.[languageVersion]?.maxWidth || 100);
    setSetSize(false);
  };

  const handleReplaceClick = () => {
    setImageVisible(false);
    setReplace(true);

    setTimeout(() => {
      const element = document.querySelector(
        `.block[data-block-id="${blockId}"]`
      ) as HTMLElement;
      if (element) {
        const elementTop = element.getBoundingClientRect().top;
        if (elementTop < 0) {
          scrollToElement({ element, offset: 200 });
        }
      }
    }, 100);
  };

  const handleReplaceCancel = () => {
    setImageVisible(true);
    setReplace(false);
  };

  const handleSetSizeClick = () => {
    const wrapperWidth = imageWrapper.current.clientWidth;
    const img = imageWrapper.current.querySelector('img');
    let wrapperHeight = img.naturalHeight;
    if (img.naturalWidth > wrapperWidth) {
      wrapperHeight = (wrapperWidth / img.naturalWidth) * img.naturalHeight;
    }
    setImageWrapperHeight(wrapperHeight);
    setSetSize(true);
  };

  const handleSetSizeChange = (value) => {
    setMaxWidth(value);
  };

  const handleSetSizeSave = () => {
    setSetSize(false);
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: { ...data.locales[languageVersion], maxWidth }
      }
    });
  };

  const handleUploadSuccess = ({ url }) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          file: { url: url }
        }
      }
    });
    setReplace(false);
    setShowLanguageSpecificUploader(false);
  };

  const handleRemoveLanguageVersionSpecificImage = () => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...defaultBlockData.image.locales[languageVersion]
        }
      }
    });
    setReplace(false);
    setShowLanguageSpecificUploader(false);
  };

  return (
    <div className="image">
      {!imageUrl || replace || showLanguageSpecificUploader ? (
        <div>
          <FileUploader
            allowedFileTypes={['image/*']}
            autoProceed={true}
            disabled={isPreview}
            dragDrop={true}
            icon="file-image"
            id={`image-uploader-${blockId}`}
            keyPrefix="uploads/images/original/"
            onUploadSuccess={handleUploadSuccess}
            placeholder={messages.editorBlockImageChooseFile}
            uuidAsFilename={true}
          />
        </div>
      ) : (
        <div>
          <div style={{ display: imageVisible ? 'none' : 'block' }}>
            <LoadingIndicator
              borderWidth={2}
              color={theme.textColorLighter}
              size={20}
              style={{ padding: 10 }}
            />
          </div>

          <div style={{ display: imageVisible ? 'block' : 'none' }}>
            <EditorBlockImageContent
              altTextVisible={altTextVisible}
              blockId={blockId}
              imageAltText={data.locales[languageVersion].translatable.altText}
              imageCaption={data.locales[languageVersion].translatable.caption}
              imageWrapperHeight={imageWrapperHeight}
              imageWrapperRef={(el) => (imageWrapper.current = el)}
              isPreview={isPreview}
              onAltTextChange={handleAltTextChange}
              onCaptionChange={handleCaptionChange}
              onImageLoad={handleImageLoad}
              maxWidth={maxWidth}
              setSize={setSize}
              {...(shouldShowDefaultImage
                ? {
                    imageAltTextReference:
                      data.locales[course.language].translatable.altText,
                    imageCaptionReference:
                      data.locales[course.language].translatable.caption,
                    imageUrl: data.locales[course.language].file.url
                  }
                : {
                    imageUrl: data.locales[languageVersion].file.url
                  })}
            />
          </div>
        </div>
      )}

      {!isPreview && imageUrl && !shouldShowDefaultImage ? (
        <EditorBlockImageControls
          altTextVisible={altTextVisible}
          blockId={blockId}
          crop={crop}
          cropLoading={editorBlockIdForCropping === blockId}
          maxWidth={maxWidth}
          onAltTextToggle={handleAltTextToggle}
          onCropCancel={handleCropCancel}
          onCropClick={handleCropClick}
          onCropSave={handleCropSave}
          onSetSizeCancel={handleSetSizeCancel}
          onSetSizeChange={handleSetSizeChange}
          onSetSizeClick={handleSetSizeClick}
          onSetSizeSave={handleSetSizeSave}
          onReplaceClick={handleReplaceClick}
          onReplaceCancel={handleReplaceCancel}
          replace={replace}
          setSize={setSize}
        />
      ) : null}

      {editorLanguageActive ? (
        <EditorBlockTranslationReplace
          customStatusText={messages.blocksImageUsingLanguageSpecific}
          defaultStatusText={messages.blocksImageUsingDefaultLanguage}
          downloadableFileUrl={data.locales[languageVersion]?.file?.url}
          hasCustomValue={!shouldShowDefaultImage}
          icon={['far', 'file-image']}
          onCancel={handleLanguageVersionReplaceCancel}
          onRemove={handleRemoveLanguageVersionSpecificImage}
          onReplace={handleLanguageVersionReplaceClick}
          showCancel={showLanguageSpecificUploader}
          useDetachedStyles={true}
        />
      ) : null}
    </div>
  );
};

export default EditorBlockImage;
